import { isRulerLineType, RulerLineType, SingleLineType, WallConnectionEnd, WallConnectionStart, WallType } from "../../types/floorplannerTypes";
import * as THREE from 'three';
import { floorplannerStore } from '../../store/floorplannerStore';
import { editorStore } from "../../store/editorStore";
import { applyAlignmentAfterSnap } from "./applyAlignmentAfterSnap";
import { applyWallPivot } from "./applyWallPivot";
import { runInAction } from "mobx";
import { renderStore } from "../../store/renderStore";
import { transformArcControlPoint } from "../../utils/arcUtils";

const snapAngle = (angle: number, snapInterval: number) => {
    return Math.round(angle / snapInterval) * snapInterval;
};

/**
 * Drag the start handle of the object, it will move the start of the object maintaining connections and updates their positions
 *
 * SNAPPING BEHAVIOR:
 * - Snapping preserves wall length for endpoint connections
 * - Edge connections are recorded at wall edges, not centers
 * - Uses removeEdgeConnection/addEdgeConnection for endpoint connections
 * - Mid-point connections use center-based snapping
 * - The system intelligently chooses connection type based on snap location
 *
 * @param source The object to drag
 * @param startOffsetX The start x offset of the object
 * @param startOffsetY The start y offset of the object
 * @param newX The new x position of the object
 * @param newY The new y position of the object
 * @param dragEndFreeToConnect If the end is free to snap
 * @param isShiftPressed If the Shift key is pressed
 * @param delta The delta vector
 * @param suppressSnapping When true, skips connection operations for deferred mode
 * @returns boolean If the wall was snapped
 */
export const dragStartHandle = (
    source: WallType | SingleLineType | RulerLineType,
    startOffsetX: number,
    startOffsetY: number,
    newX: number,
    newY: number,
    dragEndFreeToConnect: boolean,
    delta: THREE.Vector2,
    suppressSnapping: boolean = false,
): boolean => {
    const object = floorplannerStore.findObjectId(source.id);
    if (!object) {
        return false;
    }
    const dragState = floorplannerStore.wallDragState.get(object.id);
    const snapTolerance = editorStore.interactionSnappingTolerance();
    const angleSnapTolerance = editorStore.interactionSnappingTolerance(0.2);
    let snapped = false;
    let newDelta = new THREE.Vector2(
        newX - startOffsetX,
        newY - startOffsetY,
    ).sub(object.start);
    const newStartPosition = object.start.clone().add(newDelta);
    let snappedWallAngle: number | undefined = undefined;

    // SNAPPING PRIORITY ORDER:
    // 1. Wall edge corners (for wall-to-wall connections) - HIGHEST PRIORITY
    // 2. 45-degree angle snapping (when within 20 degrees of snap angle)
    // 3. Alignment lines - LOWEST PRIORITY

    // Skip hysteresis during drag preview - calculate snap position deterministically based on mouse position
    // Only use hysteresis on final drop (when suppressSnapping=false)
    const maintainAlignmentLock = () => {
        const lock = dragState?.alignmentLocks?.start;
        if (!lock) {
            return;
        }
        const holdTolerance = snapTolerance;
        const releaseTolerance = snapTolerance * 1.5;
        const distance = newStartPosition.distanceTo(lock);
        if (distance <= holdTolerance) {
            newStartPosition.copy(lock);
        } else if (distance >= releaseTolerance && dragState?.alignmentLocks) {
            dragState.alignmentLocks.start = undefined;
        }
    };

    if (editorStore.isShiftPressed && dragState?.alignmentLocks?.start) {
        dragState.alignmentLocks.start = undefined;
    } else if (!suppressSnapping) {
        // Only apply hysteresis on final drop, not during drag preview
        maintainAlignmentLock();
    }

    let edgeSnapApplied = false;
    let pendingPositions: { start: THREE.Vector2; end: THREE.Vector2 } | null = null;
    const anchorEnd = dragState?.fixedEndpoints?.end ?? object.end;
    let pendingEndPosition = anchorEnd.clone();

    // PRIORITY 1: Wall edge corner snapping (highest priority)
    // Check for edge snap BEFORE angle snapping - wall connections take priority
    if (!suppressSnapping && dragEndFreeToConnect && !isRulerLineType(object)) {
        // Edge snapping is the default mode unless ALT/OPTION key is pressed
        // Also check that Shift is not pressed (Shift disables all snapping)
        const shouldUseEdgeSnapping = !editorStore.isAltPressed && !editorStore.isShiftPressed;
        if (shouldUseEdgeSnapping) {
            // Edge mode: use edge-based snapping (default) - NO fallback to center
            const snapCandidate = floorplannerStore.getEdgeSnappingPositionForDrag(object.id, "start", newStartPosition);
            const snapPoint = snapCandidate?.snappingPosition;
            if (snapPoint && newStartPosition.distanceTo(snapPoint) < snapTolerance) {
                // Calculate the snap delta
                const snapDelta = new THREE.Vector2().subVectors(snapPoint, newStartPosition);
                // Only move the dragged endpoint (start), keep the other end fixed
                newStartPosition.add(snapDelta);
                // pendingEndPosition stays at anchorEnd (already initialized above)
                pendingPositions = {
                    start: newStartPosition.clone(),
                    end: pendingEndPosition.clone(),
                };
                snapped = true;
                edgeSnapApplied = true;
                floorplannerStore.storeEdgeSnappingPreview(object.id, "start", snapCandidate);
            } else {
                floorplannerStore.storeEdgeSnappingPreview(object.id, "start", null);
            }
            // IMPORTANT: Do NOT fall back to center snapping in edge mode
            // If no edge snap found, simply don't snap (user can press ALT for center mode)
        } else {
            floorplannerStore.storeEdgeSnappingPreview(object.id, "start", null);
            // Center mode: use center-based snapping (when ALT/OPTION is pressed)
            const snapPoint = floorplannerStore.getSnappingPositionForDrag(object.id, WallConnectionStart, newStartPosition);
            if (snapPoint && newStartPosition.distanceTo(snapPoint) < snapTolerance) {
                newStartPosition.copy(snapPoint);
                snapped = true;
                edgeSnapApplied = true; // Mark as applied to skip angle snapping
            }
        }
    }

    // PRIORITY 2: 45-degree angle snapping (only if no edge snap was applied)
    // Skip angle snapping if we already snapped to a wall edge corner
    if (!edgeSnapApplied && !editorStore.isShiftPressed) {
        // Calculate the angle between the end and current mouse position
        const deltaX = newStartPosition.x - object.end.x;
        const deltaY = newStartPosition.y - object.end.y;
        const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI); // Convert to degrees

        // Get the current distance between the start and end position
        const currentDistance = newStartPosition.distanceTo(object.end);

        // Normalize the angle to a positive value between 0 and 360
        const normalizedAngle = (angle + 360) % 360;

        // Snap the angle to the nearest 45 degrees
        const snapAngles = [0, 45, 90, 135, 180, 225, 270, 315, 360];
        let closestSnapAngle = snapAngles.reduce((prev, curr) => Math.abs(curr - normalizedAngle) < Math.abs(prev - normalizedAngle) ? curr : prev);

        // Convert the snapped angle to radians
        const radianSnapAngle = closestSnapAngle * (Math.PI / 180);

        // Calculate the snapped start position (along the nearest 45-degree line)
        const snapStartX = object.end.x + currentDistance * Math.cos(radianSnapAngle);
        const snapStartY = object.end.y + currentDistance * Math.sin(radianSnapAngle);

        // Calculate the distance between the current mouse position and the snapped position
        const snapDistance = Math.sqrt(Math.pow(newStartPosition.x - snapStartX, 2) + Math.pow(newStartPosition.y - snapStartY, 2));

        // If the distance to the snapped angle is less than 0.2, snap to the closest 45-degree angle
        if (snapDistance < angleSnapTolerance) {
            newStartPosition.x = snapStartX;
            newStartPosition.y = snapStartY;
            // Store the snapped angle for alignment filtering (alignment axes within 20° of wall angle are skipped)
            snappedWallAngle = closestSnapAngle === 360 ? 0 : closestSnapAngle;
        }
    }

    const intendedStartPosition = newStartPosition.clone();

    pendingPositions = pendingPositions ?? {
        start: newStartPosition.clone(),
        end: pendingEndPosition.clone(),
    };

    // Skip alignment check if we applied an edge snap (connection priority)
    // Note: angle snapping no longer skips alignment - instead, alignment axes within 20° of wall angle are filtered
    const skipAlignment = edgeSnapApplied;

    const alignmentResult = skipAlignment ? { applied: false, positions: null } : applyAlignmentAfterSnap({
        objectId: object.id,
        connectionEnd: WallConnectionStart,
        alignmentKey: "start",
        intendedPoint: intendedStartPosition,
        suppressSnapping,
        existingPositions: pendingPositions,
        snappedWallAngle,
    });

    const finalPositions = alignmentResult.positions ?? pendingPositions;
    if (!finalPositions) {
        return snapped;
    }

    const positionsWithPivot = object.type === "wall"
        ? applyWallPivot(object as WallType, finalPositions, "start")
        : finalPositions;

    const transformedControlPoint = object.type === "wall" && object.lineForm === "arc" && object.controlPoint
        ? transformArcControlPoint(
            object.controlPoint,
            object.start,
            object.end,
            positionsWithPivot.start,
            positionsWithPivot.end,
        )
        : undefined;
    const shouldUpdateControlPoint = !!(
        transformedControlPoint &&
        object.controlPoint &&
        transformedControlPoint.distanceTo(object.controlPoint) > 1e-6
    );

    if (object.type === "wall") {
        // Force full geometry update for the dragged wall to ensure corners update correctly
        floorplannerStore.setObjectProperty(object.id, "cornerState", undefined);
    }
    floorplannerStore.updateObjectPosition(object.id, positionsWithPivot.start.clone(), positionsWithPivot.end.clone(), { bypassEdgeConstraints: true });
    if (shouldUpdateControlPoint && transformedControlPoint) {
        floorplannerStore.setObjectProperty(object.id, "controlPoint", transformedControlPoint);
    }
    if (object.type === "wall") {
        renderStore.composeWallShape(object.id, undefined, true);
        if (!renderStore.isDragging) {
            renderStore.trimShapes(object as WallType, undefined, undefined, { force: true });
            renderStore.generateOutlineSegments(object.id);
        } else {
            renderStore.generateOutlineSegments(object.id);
            renderStore.requestInvalidation(object.id);
        }
    }

    // Propagate movement to connected walls (maintain connections)
    // Calculate the movement delta from the original position to preserve edge-to-edge relationships
    const movementDelta = new THREE.Vector2().subVectors(positionsWithPivot.start, object.start);
    const connectedIds = floorplannerStore.getWallsConnectedAtPosition(object.id, WallConnectionStart);
    connectedIds.forEach(id => {
        const connectedWall = floorplannerStore.findObjectId(id);
        if (connectedWall && connectedWall.type === 'wall') {
            let cStart = connectedWall.start.clone();
            let cEnd = connectedWall.end.clone();
            let modified = false;

            // Check based on edge connections/geometric proximity which end to update
            // For edge connections, apply the movement delta to preserve the edge-to-edge offset
            // This prevents the connected wall from jumping to center position

            // Explicit edge connection check
            const edgeConn = connectedWall.edgeConnections?.find(c => c.targetWallId === object.id && c.targetEndpoint === "start");
            if (edgeConn) {
                // Apply movement delta to preserve edge-to-edge relationship
                if (edgeConn.sourceEndpoint === "start") {
                    cStart.add(movementDelta);
                    modified = true;
                } else {
                    cEnd.add(movementDelta);
                    modified = true;
                }
            } else {
                // Fallback to geometric check for legacy/center connections
                // For center connections, copy the center position directly
                if (connectedWall.start.distanceTo(object.start) < 0.1) {
                    cStart.copy(positionsWithPivot.start);
                    modified = true;
                } else if (connectedWall.end.distanceTo(object.start) < 0.1) {
                    cEnd.copy(positionsWithPivot.start);
                    modified = true;
                }
            }

            if (modified) {
                floorplannerStore.updateObjectPosition(connectedWall.id, cStart, cEnd, { bypassEdgeConstraints: true });

                // Force full geometry update for connected wall to maintain correct connections during drag
                floorplannerStore.setObjectProperty(connectedWall.id, "cornerState", undefined);
                renderStore.composeWallShape(connectedWall.id, undefined, true);
                if (!renderStore.isDragging) {
                    renderStore.trimShapes(connectedWall, undefined, undefined, { force: true });
                    renderStore.generateOutlineSegments(connectedWall.id);
                } else {
                    renderStore.generateOutlineSegments(connectedWall.id);
                    renderStore.requestInvalidation(connectedWall.id);
                }
            }
        }
    });

    if (alignmentResult.applied) {
        snapped = true;
        newStartPosition.copy(positionsWithPivot.start);
        // Only set alignment locks on final drop, not during drag preview
        if (!suppressSnapping && dragState) {
            dragState.alignmentLocks = dragState.alignmentLocks || {};
            dragState.alignmentLocks.start = positionsWithPivot.start.clone();
        }
    }

    if (edgeSnapApplied) {
        // Only re-snap if there's no existing edge connection at this endpoint
        // This prevents edge-to-edge connections from being recalculated to center during drag
        const sourceWall = floorplannerStore.findObjectId(object.id) as WallType | undefined;
        const hasExistingEdgeConnection = sourceWall?.edgeConnections?.some(c => c.sourceEndpoint === "start");
        if (!hasExistingEdgeConnection) {
            floorplannerStore.snapToOtherObjectsEdgeBased(object.id, WallConnectionStart, positionsWithPivot.start.clone(), true, { allowDuringDeferred: false });
        }
        const refreshedStart = floorplannerStore.getEdgeSnappingPositionForDrag(object.id, "start", positionsWithPivot.start.clone());
        floorplannerStore.storeEdgeSnappingPreview(object.id, "start", refreshedStart);
    }

    // Skip connection updates when snapping is suppressed
    if (!suppressSnapping) {
        // Update all walls connected at the end position
        floorplannerStore.getWallsConnectedAtPosition(object.id, WallConnectionEnd).forEach(id => {
            floorplannerStore.objectNeedsUpdate(id);
        });
    }

    // Set property panel values for the new length
    runInAction(() => {
        editorStore.setWhichEndToUpdate("start");
        if (object.type === "singleLine") {
            const lineLength = floorplannerStore.singleLineLength(object.id);
            editorStore.setSingleLineEditingLengthPanel(lineLength);
        } else if (object.type === "rulerLine") {
            const lineLength = floorplannerStore.rulerLineLength(object.id);
            editorStore.setRulerEditingLengthPanel(lineLength);
        } else if (object.type === "wall" && !suppressSnapping) {
            const wallLength = floorplannerStore.wallLength(object);
            editorStore.setWallEditingLengthPanel(wallLength);
        }
    });

    delta.set(newDelta.x, newDelta.y);
    return snapped;
}
